import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { AVAILABLE_LOCALES, ensureLocaleLoaded, normalizeToSupported, type SupportedLocale } from '@/i18n';

function Flag({ svg }: { svg: string }) {
  return (
    <span
      className="inline-block h-[14px] w-[20px] shrink-0 overflow-hidden rounded-[2px] border border-black/10"
      dangerouslySetInnerHTML={{ __html: svg }}
    />
  );
}

interface LanguageSelectorProps {
  /** Callback opcional al cambiar; se ejecuta después de `i18n.changeLanguage`. */
  onChange?: (lang: SupportedLocale) => void;
  /** Sobrescribe estilos posicionales cuando no se usa el `<GlobalLanguageSelector />`. */
  className?: string;
}

/**
 * Selector de idioma tipo dropdown (patrón ManuJob):
 * - Botón con bandera + código en mayúsculas + arrow.
 * - Al hacer click abre un menú con los idiomas restantes.
 * - Click fuera → cierra.
 */
export function LanguageSelector({ onChange, className = '' }: LanguageSelectorProps) {
  const { i18n } = useTranslation();
  const [open, setOpen] = useState(false);
  const wrapperRef = useRef<HTMLDivElement>(null);

  const current = normalizeToSupported(i18n.language);
  const currentLocale = AVAILABLE_LOCALES.find((l) => l.code === current) ?? AVAILABLE_LOCALES[0]!;
  const others = AVAILABLE_LOCALES.filter((l) => l.code !== current);

  // Cerrar al hacer click fuera
  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (!wrapperRef.current) return;
      if (!wrapperRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  const pick = async (code: SupportedLocale) => {
    setOpen(false);
    // Cargar el resource bundle si es un locale lazy (no bundled) — sin
    // esto la UI se queda mostrando el fallback tras el changeLanguage.
    await ensureLocaleLoaded(code);
    await i18n.changeLanguage(code);
    onChange?.(code);
  };

  return (
    <div ref={wrapperRef} className={`relative ${className}`}>
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label={i18n.t('common.language')}
        className="flex items-center gap-1.5 rounded-md border border-[#DDE2F5] bg-white px-2.5 py-1.5 text-[13px] font-medium text-[#1A1A1A] shadow-sm transition-colors hover:bg-[#F4F6FF]"
      >
        <Flag svg={currentLocale.flag} />
        <span>{currentLocale.code.toUpperCase()}</span>
        <span className="ml-0.5 text-[10px] text-[#6B6B80]">▾</span>
      </button>
      {open && (
        <div
          role="listbox"
          className="absolute right-0 top-full z-[10001] mt-1 min-w-[150px] overflow-hidden rounded-md border border-[#DDE2F5] bg-white shadow-lg"
        >
          {others.map((loc) => (
            <button
              key={loc.code}
              type="button"
              role="option"
              aria-selected={false}
              onClick={() => { void pick(loc.code); }}
              className="flex w-full items-center gap-2 px-3.5 py-2.5 text-left text-[13px] text-[#1A1A1A] transition-colors hover:bg-[#F4F6FF]"
            >
              <Flag svg={loc.flag} />
              <span>{loc.label}</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

/**
 * Wrapper fijo top-right para usar en pantallas pre-login (Login, Register,
 * Forgot/Reset password, invitación). Dentro del shell autenticado no se
 * monta — el usuario cambia idioma desde Profile.
 */
export function PublicLanguageSelector() {
  return (
    <div
      className="fixed right-3 z-[9998]"
      style={{ top: 'calc(env(safe-area-inset-top) + 12px)' }}
    >
      <LanguageSelector />
    </div>
  );
}

export default LanguageSelector;
